import {
   ApiResponse,
   createApiRequest,
   Header,
   getHeaderFromSessionStorage,
} from "@/api"
import { API_URL, Endpoints } from "@/constants/endpoints"
import { HttpMethods } from "@/constants/globals"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { LogType } from "@/types/logs"

export type LogChangeField =
   | "name"
   | "priority"
   | "highLimit"
   | "highTimespan"
   | "cardColor"
   | "typefaceColor"
   | "soundClip"
   | "soundFrequency"
   | "sourceConfiguration"
   | "iconColor"
   | "parents"
   | "type"
   | "address"
   | "levelId"
   | "maintenance"
   | "vacant"
   | "sensor"
   | "sensorTypes"
   | "mainThreshold"
   | "secondaryThreshold"
   | "occupied"
   | "sharedWithExternal"
   | "category"
   | "recipients"
   | "message"
   | "subject"
   | "active"
   | (string & {})

export type LogRecipient = {
   id: string
   email: string
   firstName: string
   lastName: string
   phone: {
      prefix: string
      number: string
   }
   role: string | null
   status: string
}

export type LogChangeItem = {
   field: LogChangeField
   newValue: string | number | boolean | (string | number)[] | LogRecipient[]
   oldValue:
      string | number | boolean | (string | number)[] | LogRecipient[] | null
}

export type LogAlertData = {
   configurationId: string
   name: string
   note?: string
   priority?: number
   status: string
   systemConditionType: AlertSystemConditionType | null
   levelId: string | null
   levelName: string | null
   traceId: string | null
   customerEmailNotification: boolean
}

export interface LogScheduleWindow {
   startTime: string
   endTime: string
   weekdays: number[]
}

export interface LogSchedule {
   level: { id: string; name: string }
   timezone: string
   windows: LogScheduleWindow[]
}

export interface LogResponse {
   alertConfigurationId?: string
   alertData?: LogAlertData
   alertName?: string
   configurationPriority?: number
   cardColor?: string
   cellName?: string
   changedBy?: string
   changedByName?: string
   userName?: string
   changes?: LogChangeItem[]
   event: string
   hasCustomConfiguration?: boolean
   timestamp: string
   logType: LogType | string
   deviceId?: string
   serialNumber?: string
   type?: "respiratoryRate" | "heartRate"
   levelId?: string
   vacant?: boolean
   maintenance?: boolean
   name?: string
   note?: string
   schedule?: LogSchedule | null
   previousSchedule?: LogSchedule | null
}

export async function requestCellLogs(
   levelId: string,
   timezone: string,
   startDate?: string,
   endDate?: string
): Promise<ApiResponse<LogResponse[]>> {
   const queryParams = []
   if (startDate) {
      queryParams.push(`startDate=${startDate}`)
   }
   if (endDate) {
      queryParams.push(`endDate=${endDate}`)
   }
   queryParams.push(`timezone=${encodeURIComponent(timezone)}`)
   const response = await createApiRequest(
      `${API_URL}${Endpoints.LEVELS}${levelId}/logs/${queryParams.length > 0 ? `?${queryParams.join("&")}` : ""}`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}

export async function requestInmateLogs(
   inmateId: string,
   timezone: string,
   startDate?: string,
   endDate?: string
): Promise<ApiResponse<LogResponse[]>> {
   const queryParams = []
   if (startDate) {
      queryParams.push(`startDate=${startDate}`)
   }
   if (endDate) {
      queryParams.push(`endDate=${endDate}`)
   }
   queryParams.push(`timezone=${encodeURIComponent(timezone)}`)
   const response = await createApiRequest(
      `${API_URL}${Endpoints.INMATES}${inmateId}/logs/${queryParams.length > 0 ? `?${queryParams.join("&")}` : ""}`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.FACILITY_ID)
   )
   return response
}
